import * as React from 'react'
import Link from '@mui/material/Link'

import { isKeywordGraphApp, appProductName } from '../appVariant'
import {
    ConnectCard,
    SetupPanel,
    SetupSteps,
    SetupStep,
    ClientSwitcher,
    CodeSnippet,
    StepNote,
    StepText,
} from './connectCards'

// MCP server connection details, per app variant.
const mcpConfig = isKG => ({
    domain: isKG ? 'mcp.keywordgraph.com' : 'mcp.infranodus.com',
    pkg: isKG ? 'keywordgraph-mcp-server' : 'infranodus-mcp-server',
    serverKey: isKG ? 'keywordgraph' : 'infranodus',
    envVar: isKG ? 'KEYWORDGRAPH_API_KEY' : 'INFRANODUS_API_KEY',
})

const apiKeyLink = (
    <Link href="/api-access" underline="hover" target="_blank">
        API key
    </Link>
)

export function McpServerCard({ onHide, onApiAccessPage }) {
    const isKG = isKeywordGraphApp()
    const { domain, pkg, serverKey, envVar } = mcpConfig(isKG)
    const url = `https://${domain}`
    const cursorConfig = `{
  "mcpServers": {
    "${serverKey}": {
      "url": "${url}"
    }
  }
}`
    const jsonConfig = `{
  "mcpServers": {
    "${serverKey}": {
      "command": "npx",
      "args": ["-y", "${pkg}"],
      "env": {
        "${envVar}": "your-api-key"
      }
    }
  }
}`
    const mcpClients = {
        claude: {
            label: 'Claude',
            code: url,
            caption: (
                <>
                    Add this URL as a connector via{' '}
                    <strong>Settings &gt; Connectors</strong> in Claude Web /
                    Desktop and authenticate with your {apiKeyLink}.
                </>
            ),
        },
        chatgpt: {
            label: 'ChatGPT',
            code: url,
            caption: (
                <>
                    Open the ChatGPT{' '}
                    <Link
                        href="https://chatgpt.com/#settings/Connectors"
                        underline="hover"
                        target="_blank"
                    >
                        Apps
                    </Link>{' '}
                    page &gt; <strong>Advanced Settings</strong> &gt; enable{' '}
                    <strong>Developer Mode</strong> &gt;{' '}
                    <strong>Create App</strong>, paste this URL (leave the OAuth
                    fields empty), click Connect, and authenticate with your{' '}
                    {apiKeyLink}.
                </>
            ),
        },
        'claude-code': {
            label: 'Claude Code',
            code: `claude mcp add ${serverKey} -s user --transport http ${url}/mcp`,
            caption: (
                <>
                    Then run <code>/mcp</code> in Claude Code and pick
                    &quot;Authenticate&quot; — the browser OAuth flow asks for
                    your {apiKeyLink}.
                </>
            ),
        },
        cursor: {
            label: 'Cursor',
            code: cursorConfig,
            caption: (
                <>
                    Add this to <code>~/.cursor/mcp.json</code> (or{' '}
                    <strong>Cursor Settings &gt; MCP</strong>), then click
                    &quot;Login&quot; next to the server to authenticate with
                    your {apiKeyLink}.
                </>
            ),
        },
        codex: {
            label: 'Codex',
            code: `codex mcp add ${serverKey} --url ${url}\ncodex mcp login ${serverKey}`,
            caption: (
                <>
                    Run these in your terminal — the login command opens the
                    browser OAuth flow where you enter your {apiKeyLink}.
                </>
            ),
        },
        other: {
            label: 'Other',
            code: jsonConfig,
            caption: (
                <>
                    Add this to your client&apos;s MCP config, replacing{' '}
                    <code>your-api-key</code> with your {apiKeyLink}. More
                    clients (n8n, Make, local):{' '}
                    <Link
                        href={isKG ? '/mcp' : '/mcp/deployment'}
                        underline="hover"
                        target="_blank"
                    >
                        setup guides
                    </Link>
                    .
                </>
            ),
        },
    }
    const [mcpClient, setMcpClient] = React.useState('claude')
    const selected = mcpClients[mcpClient]
    const firstPrompt = isKG
        ? `Using the KeywordGraph MCP server, run generate_seo_report for [your topic] and list the topical clusters the top search results miss.`
        : `Using the InfraNodus MCP server, build a knowledge graph of the text below with generate_knowledge_graph, then run generate_content_gaps and tell me which two topics are never connected and what question would bridge them.\n\n[paste your text]`

    return (
        <SetupPanel title="Use in your LLM via MCP server" onHide={onHide}>
            <SetupSteps>
                <SetupStep n={1} title="Get your API key">
                    <StepText>
                        {onApiAccessPage ? (
                            'Copy your API key from the panel on this page.'
                        ) : (
                            <>
                                <Link
                                    href="/api-access"
                                    underline="hover"
                                    target="_blank"
                                >
                                    Get your API key
                                </Link>{' '}
                                — the client asks for it when you connect.
                            </>
                        )}
                    </StepText>
                </SetupStep>
                <SetupStep n={2} title="Connect the MCP server">
                    <ClientSwitcher
                        options={Object.fromEntries(
                            Object.entries(mcpClients).map(([k, c]) => [
                                k,
                                c.label,
                            ])
                        )}
                        value={mcpClient}
                        onChange={setMcpClient}
                    />
                    <CodeSnippet text={selected.code} />
                    <StepNote>{selected.caption}</StepNote>
                </SetupStep>
                <SetupStep n={3} title="Try your first prompt">
                    <StepText>
                        Paste this into your chat. Naming the tools just makes
                        the first run predictable:
                    </StepText>
                    <CodeSnippet text={firstPrompt} wrap />
                    <StepText sx={{ mt: 0.75, mb: 0 }}>
                        More on the{' '}
                        <Link href="/mcp" underline="hover" target="_blank">
                            MCP server page
                        </Link>
                        {isKG ? null : (
                            <>
                                {' '}
                                ·{' '}
                                <Link
                                    href="/mcp/tools"
                                    underline="hover"
                                    target="_blank"
                                >
                                    all tools
                                </Link>
                            </>
                        )}
                    </StepText>
                </SetupStep>
            </SetupSteps>
        </SetupPanel>
    )
}

/**
 * The MCP entry as it appears on a page: a collapsed card that swaps IN PLACE
 * with the full setup panel, so opening it never reorders its neighbours.
 * `onApiAccessPage` is for /api-access itself, where the key is already on
 * screen and a "get your API key" link would just reopen the same page.
 */
export default function McpServerConnect({
    defaultOpen = false,
    onApiAccessPage = false,
}) {
    const [open, setOpen] = React.useState(defaultOpen)
    const isKeywordGraph = isKeywordGraphApp()

    return open ? (
        <McpServerCard
            onHide={() => setOpen(false)}
            onApiAccessPage={onApiAccessPage}
        />
    ) : (
        <ConnectCard
            title="Use in your LLM via MCP server"
            description={`Add ${appProductName()} as a connector in Claude, ChatGPT, Cursor or n8n and build graphs straight from your chat.`}
            snippet={`https://${mcpConfig(isKeywordGraph).domain}`}
            onOpen={() => setOpen(true)}
        />
    )
}
